<template>
  <div class="run-detail">
    <div v-if="!run" class="run-detail-empty">No run selected.</div>
    <template v-else>
      <div class="detail-body">
        <div v-if="failedMessage" class="run-error">
          {{ failedMessage }}
        </div>

        <div
          v-if="run.object_context !== null && run.object_context !== undefined"
          class="step-result object-context"
        >
          <CompactCollapsibleSection
            label="Object context"
            :expanded="objectContextExpanded"
            @toggle="objectContextExpanded = !objectContextExpanded"
          >
            <pre class="step-output">{{ formatJson(run.object_context) }}</pre>
          </CompactCollapsibleSection>
        </div>

        <div
          v-if="
            run.step_results.length === 0 &&
            (run.status.status === 'completed' ||
              run.status.status === 'failed')
          "
          class="empty"
        >
          No step results recorded.
        </div>

        <div
          v-for="(step, stepIdx) in run.step_results"
          :key="stepIdx"
          class="step-result"
        >
          <div class="step-header">
            <BaseButton
              kind="auxiliary"
              :text="step.name"
              class="step-name--toggle"
              @click="toggleStep(stepIdx)"
            >
              <template #icon>
                <ChevronRightIcon
                  class="step-chevron"
                  :class="{ expanded: isStepExpanded(stepIdx) }"
                />
              </template>
            </BaseButton>
            <BaseButton
              kind="auxiliary"
              class="copy-btn"
              :text="copiedKey ? 'Copied!' : 'Copy'"
              @click="copyAll"
            >
              <template #icon><CopyIcon /></template>
            </BaseButton>
          </div>

          <template v-if="isStepExpanded(stepIdx)">
            <pre v-if="step.output" class="step-output">{{ step.output }}</pre>
            <CompactCollapsibleSection
              v-if="step.tool_calls?.length"
              class="tool-call-group"
              :label="toolCallSummary(step.tool_calls)"
              :expanded="isToolCallGroupExpanded(stepIdx)"
              @toggle="toggleToolCallGroup(stepIdx)"
            >
              <div class="tool-call-group-details">
                <AutomationRunToolCall
                  v-for="(call, callIdx) in step.tool_calls"
                  :key="callIdx"
                  :call="call"
                />
              </div>
            </CompactCollapsibleSection>
          </template>
        </div>

        <div v-if="run.status.status === 'pending_resume'" class="step-result">
          <AutomationRunToolCall
            v-for="(call, callIdx) in run.status.tool_calls ?? []"
            :key="callIdx"
            :call="call"
            :organization-id="organizationId"
            :workspace-id="workspaceId"
          />
          <AutomationRunToolCall
            :call="pendingResumeAskUserCall"
            :organization-id="organizationId"
            :workspace-id="workspaceId"
          />
        </div>

        <AutomationRunPendingInput
          v-if="run.status.status === 'pending_user_input'"
          :questions="run.status.request.questions"
          :organization-id="organizationId"
          :run-id="run.id"
          :workspace-id="workspaceId"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  reactive,
  ref,
  toRef,
  watch,
  type PropType,
} from "vue";

import type {
  AiAutomationRunToolCall,
  AutomationRunId,
  OrgId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import AutomationRunPendingInput from "@/components/ai/AutomationRunPendingInput.vue";
import { normalizeToolCall } from "@/components/ai/automationRunToolCall";
import AutomationRunToolCall from "@/components/ai/AutomationRunToolCall.vue";
import CompactCollapsibleSection from "@/components/ai/CompactCollapsibleSection.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import * as inject from "@/components/inject";
import { getOrganizationDefaultWorkspace } from "@/components/workspaces";
import { bindAsync, releasableRef, useReleaser } from "@/ref";

export default defineComponent({
  name: "AutomationRunDetail",
  components: {
    CompactCollapsibleSection,
    AutomationRunPendingInput,
    AutomationRunToolCall,
    BaseButton,
    ChevronRightIcon,
    CopyIcon,
  },
  props: {
    runId: { type: String as PropType<AutomationRunId>, required: true },
    organizationId: { type: String as PropType<OrgId>, required: true },
  },
  async setup(props) {
    const controller = inject.controller();
    const runIdRef = toRef(props, "runId");

    const releaser = useReleaser();

    const stepExpanded = reactive<Record<number, boolean>>({});
    const isStepExpanded = (si: number) => !!stepExpanded[si];
    const toggleStep = (si: number) => {
      stepExpanded[si] = !stepExpanded[si];
    };

    const toolCallGroupExpanded = reactive<Record<number, boolean>>({});
    const isToolCallGroupExpanded = (si: number) => !!toolCallGroupExpanded[si];
    const toggleToolCallGroup = (si: number) => {
      toolCallGroupExpanded[si] = !toolCallGroupExpanded[si];
    };

    const objectContextExpanded = ref(false);

    const toolCallSummary = (calls: AiAutomationRunToolCall[]): string => {
      const toolCount = calls.length;
      const toolLabel = toolCount === 1 ? "tool" : "tools";
      const uniqueNames = Array.from(
        new Set(
          calls.map((call) => {
            const normalized = normalizeToolCall(call);
            return normalized.kind === "call" ? normalized.name : "ask_user";
          })
        )
      );
      const visibleNames = uniqueNames.slice(0, 3);
      const extraCount = uniqueNames.length - visibleNames.length;
      const names = visibleNames.join(", ");
      if (names.length === 0) return `Called ${toolCount} ${toolLabel}`;
      const extra = extraCount > 0 ? `, +${extraCount} more` : "";
      return `Called ${toolCount} ${toolLabel}: ${names}${extra}`;
    };

    watch(
      runIdRef,
      () => {
        for (const key of Object.keys(stepExpanded)) {
          delete stepExpanded[Number(key)];
        }
        stepExpanded[0] = true;
        objectContextExpanded.value = false;
      },
      { immediate: true }
    );

    const copiedKey = ref(false);
    let copiedTimer: ReturnType<typeof setTimeout> | null = null;
    releaser.defer(() => {
      if (copiedTimer) clearTimeout(copiedTimer);
    });

    const run = await releaser.add(
      bindAsync(runIdRef, async (id) => {
        if (!id) return releasableRef(null);
        return await controller.ai.automation.run.get(props.organizationId, id);
      })
    );

    // Resolve the org's default workspace so rich-text question descriptions
    // (AiAutomationRunQuestion.long) can render.
    const organizationIdRef = toRef(props, "organizationId");
    const workspaceId = await releaser.add(
      bindAsync(organizationIdRef, async (orgId: OrgId) => {
        const wsId = await getOrganizationDefaultWorkspace(controller, orgId);
        return releasableRef<WorkspaceId | undefined>(wsId);
      })
    );

    const formattedDate = computed(() =>
      run.value?.started_at.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    );

    const statusClass = computed(() => {
      const s = run.value?.status.status;
      if (s === "completed") return "status--completed";
      if (s === "failed") return "status--failed";
      if (s === "pending_user_input") return "status--waiting";
      if (s === "pending_resume") return "status--resuming";
      return "status--failed";
    });

    const statusLabel = computed(() => {
      const s = run.value?.status.status;
      if (s === "completed") return "Completed";
      if (s === "failed") return "Failed";
      if (s === "pending_user_input") return "Waiting for input";
      if (s === "pending_resume") return "Resuming…";
      return "Failed";
    });

    const failedMessage = computed(() =>
      run.value?.status.status === "failed" ? run.value.status.message : null
    );

    const pendingResumeAskUserCall = computed(() => {
      if (run.value?.status.status !== "pending_resume") {
        return {
          kind: "ask_user" as const,
          response: { kind: "answers" as const, answered_questions: [] },
        };
      }
      const status = run.value.status;
      return {
        kind: "ask_user" as const,
        response: status.response,
        conversation: status.conversation,
      };
    });

    const formatJson = (value: unknown): string => {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    };

    const copyAll = async () => {
      if (!run.value) return;
      const lines: string[] = [];
      lines.push(`${formattedDate.value} — ${statusLabel.value}`);
      if (run.value.status.status === "failed") {
        lines.push(`Error: ${run.value.status.message}`);
      }
      if (
        run.value.object_context !== null &&
        run.value.object_context !== undefined
      ) {
        lines.push(
          `## Object context\n${formatJson(run.value.object_context)}`
        );
      }
      for (const step of run.value.step_results) {
        lines.push(`## ${step.name}`);
        if (step.output) lines.push(step.output);
        for (const call of step.tool_calls ?? []) {
          const normalized = normalizeToolCall(call);
          if (normalized.kind === "ask_user") {
            lines.push(`### ask_user`);
            for (const aq of normalized.answeredQuestions) {
              lines.push(`Q: ${aq.question.short}`);
              lines.push(`A:\n${aq.answers.join("\n") || "(no answer)"}`);
            }
          } else {
            lines.push(`### ${normalized.name}`);
            lines.push(`Arguments:\n${formatJson(normalized.arguments)}`);
            lines.push(`Result:\n${formatJson(normalized.result)}`);
          }
        }
      }
      await navigator.clipboard.writeText(lines.join("\n\n"));
      if (copiedTimer) clearTimeout(copiedTimer);
      copiedKey.value = true;
      copiedTimer = setTimeout(() => {
        copiedKey.value = false;
      }, 2000);
    };

    return {
      run,
      workspaceId,
      formattedDate,
      statusClass,
      statusLabel,
      failedMessage,
      pendingResumeAskUserCall,
      objectContextExpanded,
      isStepExpanded,
      toggleStep,
      isToolCallGroupExpanded,
      toggleToolCallGroup,
      toolCallSummary,
      formatJson,
      copiedKey,
      copyAll,
    };
  },
});
</script>

<style scoped lang="scss">
.run-detail {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.run-detail-empty {
  padding: 24px 20px;
  @extend %font-body-regular;

  @include themed() {
    color: t($grey-40);
  }
}

.detail-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  @include hide-scrollbar;
}

.run-error {
  @extend %font-body-regular;
  padding: 10px 12px;
  border-radius: $border-radius-m;

  @include themed() {
    background-color: t($secondary-10);
    color: t($secondary-80);
  }
}

.empty {
  @extend %font-body-regular;

  @include themed() {
    color: t($grey-40);
  }
}

.step-result {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: $border-radius-m;

  @include themed() {
    border: 1px solid t($grey-10);
  }
}

.step-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .step-name--toggle {
    flex: 1;
    min-width: 0;
  }

  .copy-btn {
    flex-shrink: 0;
    opacity: 0;
    @include transition(opacity 150ms ease-out);
  }

  &:hover .copy-btn {
    opacity: 1;
  }
}

.step-chevron {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  @include transition(transform 0.15s ease-out);

  &.expanded {
    transform: rotate(90deg);
  }
}

.tool-call-group-details {
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  margin-inline-start: 20px;
}

.step-output {
  font-family: $mono;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  padding: 8px 10px;
  border-radius: $border-radius-s;
  user-select: text;
  cursor: text;

  @include themed() {
    background-color: t($bg-alt);
    color: t($grey-80);
  }
}
</style>
